import React, { useState, useEffect } from 'react';
import { View, ScrollView, StyleSheet, Dimensions } from 'react-native';
import {
  Card,
  Text,
  Button,
  Chip,
  Surface,
  useTheme,
  ProgressBar,
  List,
  Avatar,
  Badge,
  Divider,
  IconButton,
  Menu,
  Portal,
  Dialog,
  TextInput,
} from 'react-native-paper';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useNavigation } from '@react-navigation/native';
import {
  Brain,
  Map,
  Target,
  Clock,
  BookOpen,
  Users,
  Zap,
  CheckCircle,
  ArrowRight,
  Sparkles,
  TrendingUp,
  BarChart3,
  Edit,
  Plus,
  Trash2,
  RefreshCw,
  Lightbulb,
  AlertCircle,
  Star,
  Calendar,
  Timer,
  Coffee,
  Filter,
  Search,
} from 'lucide-react-native';

interface AIJourney {
  id: string;
  title: string;
  description: string;
  status: 'generating' | 'active' | 'optimizing' | 'completed';
  progress: number;
  difficulty: 'beginner' | 'intermediate' | 'advanced';
  estimatedWeeks: number;
  timePerWeek: number;
  focusAreas: string[];
  aiScore: number;
  lastOptimized: string;
  nextOptimization: string;
  weeks: AIJourneyWeek[];
  insights: AIInsight[];
  recommendations: AIRecommendation[];
}

interface AIJourneyWeek {
  id: string;
  weekNumber: number;
  title: string;
  description: string;
  objectives: string[];
  activities: AIJourneyActivity[];
  estimatedHours: number;
  difficulty: number;
  isUnlocked: boolean;
  isCompleted: boolean;
  aiOptimization: string;
}

interface AIJourneyActivity {
  id: string;
  title: string;
  description: string;
  type: 'video' | 'reading' | 'practice' | 'assessment' | 'interactive';
  estimatedMinutes: number;
  difficulty: number;
  prerequisites: string[];
  isCompleted: boolean;
  aiRecommendation: string;
  userRating?: number;
  userFeedback?: string;
}

interface AIInsight {
  type: 'performance' | 'engagement' | 'difficulty' | 'optimization';
  message: string;
  confidence: number;
  impact: 'high' | 'medium' | 'low';
  timestamp: string;
}

interface AIRecommendation {
  type: 'content' | 'sequence' | 'difficulty' | 'timing';
  title: string;
  description: string;
  priority: 'high' | 'medium' | 'low';
  estimatedImpact: number;
  reasoning: string;
}

const { width } = Dimensions.get('window');

export function AIJourneyFlowScreen() {
  const theme = useTheme();
  const insets = useSafeAreaInsets();
  const navigation = useNavigation();

  const [selectedJourney, setSelectedJourney] = useState<AIJourney | null>(
    null
  );
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showOptimizeDialog, setShowOptimizeDialog] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedFilter, setSelectedFilter] = useState<string>('all');
  const [isGenerating, setIsGenerating] = useState(false);

  // Mock AI journeys data
  const [aiJourneys, setAiJourneys] = useState<AIJourney[]>([
    {
      id: 'journey-1',
      title: 'Espresso Mastery Path',
      description:
        'Master the art of espresso brewing with AI-optimized learning',
      status: 'active',
      progress: 65,
      difficulty: 'intermediate',
      estimatedWeeks: 8,
      timePerWeek: 6,
      focusAreas: ['Espresso', 'Grinding', 'Tamping', 'Extraction'],
      aiScore: 94,
      lastOptimized: '2024-01-15',
      nextOptimization: '2024-01-22',
      weeks: generateMockWeeks(8),
      insights: generateMockInsights(),
      recommendations: generateMockRecommendations(),
    },
    {
      id: 'journey-2',
      title: 'Pour-Over Fundamentals',
      description: 'Learn pour-over brewing from basics to advanced techniques',
      status: 'generating',
      progress: 0,
      difficulty: 'beginner',
      estimatedWeeks: 6,
      timePerWeek: 4,
      focusAreas: ['Pour-Over', 'Water Chemistry', 'Grind Size', 'Timing'],
      aiScore: 87,
      lastOptimized: '2024-01-10',
      nextOptimization: '2024-01-17',
      weeks: generateMockWeeks(6),
      insights: generateMockInsights(),
      recommendations: generateMockRecommendations(),
    },
    {
      id: 'journey-3',
      title: 'Advanced Cupping & Tasting',
      description: 'Develop professional cupping skills and palate training',
      status: 'optimizing',
      progress: 45,
      difficulty: 'advanced',
      estimatedWeeks: 10,
      timePerWeek: 8,
      focusAreas: [
        'Cupping',
        'Tasting',
        'Flavor Profiles',
        'Quality Assessment',
      ],
      aiScore: 91,
      lastOptimized: '2024-01-14',
      nextOptimization: '2024-01-21',
      weeks: generateMockWeeks(10),
      insights: generateMockInsights(),
      recommendations: generateMockRecommendations(),
    },
  ]);

  function generateMockWeeks(count: number): AIJourneyWeek[] {
    return Array.from({ length: count }, (_, i) => ({
      id: `week-${i + 1}`,
      weekNumber: i + 1,
      title: `Week ${i + 1}`,
      description: `Week ${i + 1} learning objectives and activities`,
      objectives: [
        `Objective ${i + 1}.1`,
        `Objective ${i + 1}.2`,
        `Objective ${i + 1}.3`,
      ],
      activities: generateMockActivities(),
      estimatedHours: 4 + Math.floor(Math.random() * 4),
      difficulty: 1 + Math.floor(Math.random() * 5),
      isUnlocked: i === 0,
      isCompleted: false,
      aiOptimization: `AI optimized for optimal learning flow`,
    }));
  }

  function generateMockActivities(): AIJourneyActivity[] {
    const activityTypes: AIJourneyActivity['type'][] = [
      'video',
      'reading',
      'practice',
      'assessment',
      'interactive',
    ];
    return Array.from({ length: 5 }, (_, i) => ({
      id: `activity-${i + 1}`,
      title: `Activity ${i + 1}`,
      description: `Description for activity ${i + 1}`,
      type: activityTypes[Math.floor(Math.random() * activityTypes.length)],
      estimatedMinutes: 15 + Math.floor(Math.random() * 45),
      difficulty: 1 + Math.floor(Math.random() * 5),
      prerequisites: [],
      isCompleted: false,
      aiRecommendation: `AI recommended based on your learning style`,
    }));
  }

  function generateMockInsights(): AIInsight[] {
    return [
      {
        type: 'performance',
        message: "You're performing above average in practical activities",
        confidence: 0.89,
        impact: 'high',
        timestamp: '2024-01-15T10:30:00Z',
      },
      {
        type: 'engagement',
        message: 'Video content engagement is 23% higher than reading',
        confidence: 0.92,
        impact: 'medium',
        timestamp: '2024-01-15T09:15:00Z',
      },
      {
        type: 'difficulty',
        message: 'Current difficulty level matches your skill progression',
        confidence: 0.85,
        impact: 'medium',
        timestamp: '2024-01-15T08:45:00Z',
      },
    ];
  }

  function generateMockRecommendations(): AIRecommendation[] {
    return [
      {
        type: 'content',
        title: 'Add more video tutorials',
        description: 'Your engagement is higher with visual content',
        priority: 'high',
        estimatedImpact: 0.23,
        reasoning:
          'Based on your learning style preference and engagement metrics',
      },
      {
        type: 'sequence',
        title: 'Reorganize week 4 activities',
        description: 'Current sequence may cause cognitive overload',
        priority: 'medium',
        estimatedImpact: 0.15,
        reasoning: 'Analysis of completion rates and user feedback',
      },
      {
        type: 'difficulty',
        title: 'Increase week 6 difficulty',
        description: "You're ready for more challenging content",
        priority: 'low',
        estimatedImpact: 0.08,
        reasoning: 'Performance metrics indicate skill growth',
      },
    ];
  }

  const handleCreateJourney = async () => {
    setIsGenerating(true);

    // Simulate AI journey generation
    await new Promise(resolve => setTimeout(resolve, 4000));

    const newJourney: AIJourney = {
      id: `journey-${Date.now()}`,
      title: 'AI-Generated Coffee Journey',
      description: 'Personalized learning path created by our AI',
      status: 'active',
      progress: 0,
      difficulty: 'beginner',
      estimatedWeeks: 6,
      timePerWeek: 5,
      focusAreas: ['Fundamentals', 'Brewing Methods', 'Tasting'],
      aiScore: 89,
      lastOptimized: new Date().toISOString().split('T')[0],
      nextOptimization: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000)
        .toISOString()
        .split('T')[0],
      weeks: generateMockWeeks(6),
      insights: generateMockInsights(),
      recommendations: generateMockRecommendations(),
    };

    setAiJourneys(prev => [newJourney, ...prev]);
    setIsGenerating(false);
    setShowCreateDialog(false);
  };

  const handleOptimizeJourney = async (journeyId: string) => {
    setIsGenerating(true);

    // Simulate AI optimization
    await new Promise(resolve => setTimeout(resolve, 3000));

    setAiJourneys(prev =>
      prev.map(journey =>
        journey.id === journeyId
          ? {
              ...journey,
              status: 'active' as const,
              lastOptimized: new Date().toISOString().split('T')[0],
              nextOptimization: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000)
                .toISOString()
                .split('T')[0],
              aiScore: Math.min(
                100,
                journey.aiScore + Math.floor(Math.random() * 5)
              ),
            }
          : journey
      )
    );

    setIsGenerating(false);
    setShowOptimizeDialog(false);
  };

  const getStatusColor = (status: AIJourney['status']) => {
    switch (status) {
      case 'generating':
        return theme.colors.secondary;
      case 'active':
        return theme.colors.primary;
      case 'optimizing':
        return theme.colors.tertiary;
      case 'completed':
        return theme.colors.tertiary;
      default:
        return theme.colors.outline;
    }
  };

  const getDifficultyColor = (difficulty: AIJourney['difficulty']) => {
    switch (difficulty) {
      case 'beginner':
        return theme.colors.primary;
      case 'intermediate':
        return theme.colors.secondary;
      case 'advanced':
        return theme.colors.tertiary;
      default:
        return theme.colors.outline;
    }
  };

  const filteredJourneys = aiJourneys.filter(journey => {
    const matchesSearch =
      journey.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      journey.description.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesFilter =
      selectedFilter === 'all' || journey.status === selectedFilter;
    return matchesSearch && matchesFilter;
  });

  const renderJourneyCard = (journey: AIJourney) => (
    <Card
      key={journey.id}
      style={styles.journeyCard}
      onPress={() => setSelectedJourney(journey)}
    >
      <Card.Content>
        <View style={styles.journeyHeader}>
          <View style={styles.journeyTitleRow}>
            <Text variant='titleMedium' style={styles.journeyTitle}>
              {journey.title}
            </Text>
            <View style={styles.journeyBadges}>
              <Chip
                mode='outlined'
                textStyle={{ color: getStatusColor(journey.status) }}
                style={{ borderColor: getStatusColor(journey.status) }}
                compact
              >
                {journey.status}
              </Chip>
              <Chip
                mode='outlined'
                textStyle={{ color: getDifficultyColor(journey.difficulty) }}
                style={{ borderColor: getDifficultyColor(journey.difficulty) }}
                compact
              >
                {journey.difficulty}
              </Chip>
            </View>
          </View>

          <Text variant='bodySmall' style={styles.journeyDescription}>
            {journey.description}
          </Text>
        </View>

        <View style={styles.journeyProgress}>
          <View style={styles.progressHeader}>
            <Text variant='labelMedium'>Progress</Text>
            <Text variant='labelMedium'>{journey.progress}%</Text>
          </View>
          <ProgressBar
            progress={journey.progress / 100}
            color={theme.colors.primary}
            style={styles.progressBar}
          />
        </View>

        <View style={styles.journeyMetrics}>
          <View style={styles.metricItem}>
            <Clock size={16} color={theme.colors.onSurfaceVariant} />
            <Text variant='bodySmall' style={styles.metricText}>
              {journey.estimatedWeeks} weeks
            </Text>
          </View>
          <View style={styles.metricItem}>
            <Timer size={16} color={theme.colors.onSurfaceVariant} />
            <Text variant='bodySmall' style={styles.metricText}>
              {journey.timePerWeek}h/week
            </Text>
          </View>
          <View style={styles.metricItem}>
            <Brain size={16} color={theme.colors.primary} />
            <Text variant='bodySmall' style={styles.metricText}>
              AI Score: {journey.aiScore}
            </Text>
          </View>
        </View>

        <View style={styles.focusAreas}>
          {journey.focusAreas.slice(0, 3).map((area, index) => (
            <Chip key={index} mode='outlined' compact style={styles.focusChip}>
              {area}
            </Chip>
          ))}
          {journey.focusAreas.length > 3 && (
            <Chip mode='outlined' compact style={styles.focusChip}>
              +{journey.focusAreas.length - 3} more
            </Chip>
          )}
        </View>

        <View style={styles.journeyActions}>
          <Button
            mode='contained'
            onPress={() => setSelectedJourney(journey)}
            style={styles.actionButton}
            compact
          >
            View Details
          </Button>
          {journey.status === 'active' && (
            <Button
              mode='outlined'
              onPress={() => setShowOptimizeDialog(true)}
              style={styles.actionButton}
              compact
              icon='refresh'
            >
              Optimize
            </Button>
          )}
        </View>
      </Card.Content>
    </Card>
  );

  const renderJourneyDetails = () => {
    if (!selectedJourney) return null;

    return (
      <Portal>
        <Dialog
          visible={!!selectedJourney}
          onDismiss={() => setSelectedJourney(null)}
          style={styles.dialog}
        >
          <Dialog.Title style={styles.dialogTitle}>
            {selectedJourney.title}
          </Dialog.Title>

          <Dialog.Content style={styles.dialogContent}>
            <ScrollView showsVerticalScrollIndicator={false}>
              {/* Journey Overview */}
              <Surface style={styles.overviewCard} elevation={1}>
                <View style={styles.overviewHeader}>
                  <Brain size={24} color={theme.colors.primary} />
                  <Text variant='titleMedium' style={styles.overviewTitle}>
                    AI Overview
                  </Text>
                </View>

                <View style={styles.overviewMetrics}>
                  <View style={styles.overviewMetric}>
                    <Text variant='labelSmall'>AI Score</Text>
                    <Text variant='titleLarge' style={styles.aiScore}>
                      {selectedJourney.aiScore}
                    </Text>
                  </View>
                  <View style={styles.overviewMetric}>
                    <Text variant='labelSmall'>Last Optimized</Text>
                    <Text variant='bodyMedium'>
                      {selectedJourney.lastOptimized}
                    </Text>
                  </View>
                  <View style={styles.overviewMetric}>
                    <Text variant='labelSmall'>Next Optimization</Text>
                    <Text variant='bodyMedium'>
                      {selectedJourney.nextOptimization}
                    </Text>
                  </View>
                </View>
              </Surface>

              {/* AI Insights */}
              <Surface style={styles.insightsCard} elevation={1}>
                <View style={styles.insightsHeader}>
                  <Lightbulb size={24} color={theme.colors.secondary} />
                  <Text variant='titleMedium' style={styles.insightsTitle}>
                    AI Insights
                  </Text>
                </View>

                {selectedJourney.insights.map((insight, index) => (
                  <View key={index} style={styles.insightItem}>
                    <View style={styles.insightHeader}>
                      <Chip
                        mode='outlined'
                        textStyle={{ color: getInsightColor(insight.type) }}
                        style={{ borderColor: getInsightColor(insight.type) }}
                        compact
                      >
                        {insight.type}
                      </Chip>
                      <Badge style={styles.confidenceBadge}>
                        {Math.round(insight.confidence * 100)}%
                      </Badge>
                    </View>
                    <Text variant='bodyMedium' style={styles.insightMessage}>
                      {insight.message}
                    </Text>
                    <Chip
                      mode='outlined'
                      textStyle={{ color: getImpactColor(insight.impact) }}
                      style={{ borderColor: getImpactColor(insight.impact) }}
                      compact
                    >
                      {insight.impact} impact
                    </Chip>
                  </View>
                ))}
              </Surface>

              {/* AI Recommendations */}
              <Surface style={styles.recommendationsCard} elevation={1}>
                <View style={styles.recommendationsHeader}>
                  <Star size={24} color={theme.colors.tertiary} />
                  <Text
                    variant='titleMedium'
                    style={styles.recommendationsTitle}
                  >
                    AI Recommendations
                  </Text>
                </View>

                {selectedJourney.recommendations.map((rec, index) => (
                  <View key={index} style={styles.recommendationItem}>
                    <View style={styles.recommendationHeader}>
                      <Chip
                        mode='outlined'
                        textStyle={{ color: getPriorityColor(rec.priority) }}
                        style={{ borderColor: getPriorityColor(rec.priority) }}
                        compact
                      >
                        {rec.priority}
                      </Chip>
                      <Text variant='bodySmall' style={styles.impactText}>
                        {Math.round(rec.estimatedImpact * 100)}% impact
                      </Text>
                    </View>
                    <Text
                      variant='bodyMedium'
                      style={styles.recommendationTitle}
                    >
                      {rec.title}
                    </Text>
                    <Text
                      variant='bodySmall'
                      style={styles.recommendationDescription}
                    >
                      {rec.description}
                    </Text>
                    <Text
                      variant='bodySmall'
                      style={styles.recommendationReasoning}
                    >
                      {rec.reasoning}
                    </Text>
                  </View>
                ))}
              </Surface>

              {/* Journey Weeks */}
              <Surface style={styles.weeksCard} elevation={1}>
                <View style={styles.weeksHeader}>
                  <Calendar size={24} color={theme.colors.primary} />
                  <Text variant='titleMedium' style={styles.weeksTitle}>
                    Learning Path
                  </Text>
                </View>

                {selectedJourney.weeks.map(week => (
                  <View key={week.id} style={styles.weekItem}>
                    <View style={styles.weekHeader}>
                      <Text variant='titleMedium' style={styles.weekTitle}>
                        {week.title}
                      </Text>
                      <View style={styles.weekBadges}>
                        <Chip mode='outlined' compact>
                          {week.estimatedHours}h
                        </Chip>
                        <Chip mode='outlined' compact>
                          Level {week.difficulty}
                        </Chip>
                      </View>
                    </View>
                    <Text variant='bodySmall' style={styles.weekDescription}>
                      {week.description}
                    </Text>
                    <Text variant='bodySmall' style={styles.aiOptimization}>
                      {week.aiOptimization}
                    </Text>
                  </View>
                ))}
              </Surface>
            </ScrollView>
          </Dialog.Content>

          <Dialog.Actions>
            <Button onPress={() => setSelectedJourney(null)}>Close</Button>
            <Button
              mode='contained'
              onPress={() => {
                setSelectedJourney(null);
                (navigation as any).navigate('aiAdaptiveLearning');
              }}
            >
              Start Learning
            </Button>
          </Dialog.Actions>
        </Dialog>
      </Portal>
    );
  };

  const getInsightColor = (type: AIInsight['type']) => {
    switch (type) {
      case 'performance':
        return theme.colors.primary;
      case 'engagement':
        return theme.colors.secondary;
      case 'difficulty':
        return theme.colors.tertiary;
      case 'optimization':
        return theme.colors.primary;
      default:
        return theme.colors.outline;
    }
  };

  const getImpactColor = (impact: AIInsight['impact']) => {
    switch (impact) {
      case 'high':
        return theme.colors.primary;
      case 'medium':
        return theme.colors.secondary;
      case 'low':
        return theme.colors.outline;
      default:
        return theme.colors.outline;
    }
  };

  const getPriorityColor = (priority: AIRecommendation['priority']) => {
    switch (priority) {
      case 'high':
        return theme.colors.error;
      case 'medium':
        return theme.colors.secondary;
      case 'low':
        return theme.colors.outline;
      default:
        return theme.colors.outline;
    }
  };

  return (
    <View
      style={[styles.container, { backgroundColor: theme.colors.background }]}
    >
      {/* Action Header */}
      <Surface style={styles.header} elevation={2}>
        <View style={styles.headerContent}>
          <View style={styles.headerTitle}>
            <Brain size={32} color={theme.colors.primary} />
            <View style={styles.titleContainer}>
              <Text variant='headlineMedium' style={styles.title}>
                AI Journey Flow
              </Text>
              <Text variant='bodyMedium' style={styles.subtitle}>
                Dynamic learning paths powered by artificial intelligence
              </Text>
            </View>
          </View>

          <Button
            mode='contained'
            onPress={() => setShowCreateDialog(true)}
            icon='plus'
            style={styles.createButton}
          >
            Create Journey
          </Button>
        </View>
      </Surface>

      {/* Search and Filters */}
      <Surface style={styles.searchSection} elevation={1}>
        <View style={styles.searchRow}>
          <View style={styles.searchContainer}>
            <Search size={20} color={theme.colors.onSurfaceVariant} />
            <TextInput
              placeholder='Search journeys...'
              value={searchQuery}
              onChangeText={setSearchQuery}
              style={styles.searchInput}
              mode='outlined'
              dense
            />
          </View>

          <Menu
            visible={false}
            onDismiss={() => {}}
            anchor={
              <Button
                mode='outlined'
                icon='filter'
                onPress={() => {}}
                style={styles.filterButton}
              >
                {selectedFilter}
              </Button>
            }
          >
            {['all', 'generating', 'active', 'optimizing', 'completed'].map(
              filter => (
                <Menu.Item
                  key={filter}
                  onPress={() => setSelectedFilter(filter)}
                  title={filter.charAt(0).toUpperCase() + filter.slice(1)}
                />
              )
            )}
          </Menu>
        </View>
      </Surface>

      {/* Journeys List */}
      <ScrollView
        style={styles.content}
        contentContainerStyle={styles.contentContainer}
      >
        {filteredJourneys.length > 0 ? (
          filteredJourneys.map(renderJourneyCard)
        ) : (
          <View style={styles.emptyState}>
            <Brain size={64} color={theme.colors.outline} />
            <Text variant='headlineSmall' style={styles.emptyTitle}>
              No Journeys Found
            </Text>
            <Text variant='bodyMedium' style={styles.emptySubtitle}>
              {searchQuery
                ? 'Try adjusting your search terms'
                : 'Create your first AI-powered journey'}
            </Text>
            {!searchQuery && (
              <Button
                mode='contained'
                onPress={() => setShowCreateDialog(true)}
                style={styles.emptyButton}
                icon='plus'
              >
                Create Journey
              </Button>
            )}
          </View>
        )}
      </ScrollView>

      {/* Create Journey Dialog */}
      <Portal>
        <Dialog
          visible={showCreateDialog}
          onDismiss={() => setShowCreateDialog(false)}
        >
          <Dialog.Title>Create AI Journey</Dialog.Title>
          <Dialog.Content>
            <Text variant='bodyMedium'>
              Our AI will analyze your preferences and create a personalized
              learning journey.
            </Text>
          </Dialog.Content>
          <Dialog.Actions>
            <Button onPress={() => setShowCreateDialog(false)}>Cancel</Button>
            <Button
              mode='contained'
              onPress={handleCreateJourney}
              loading={isGenerating}
              disabled={isGenerating}
            >
              {isGenerating ? 'Generating...' : 'Create Journey'}
            </Button>
          </Dialog.Actions>
        </Dialog>
      </Portal>

      {/* Optimize Journey Dialog */}
      <Portal>
        <Dialog
          visible={showOptimizeDialog}
          onDismiss={() => setShowOptimizeDialog(false)}
        >
          <Dialog.Title>Optimize Journey</Dialog.Title>
          <Dialog.Content>
            <Text variant='bodyMedium'>
              Our AI will analyze your progress and optimize your learning path
              for better results.
            </Text>
          </Dialog.Content>
          <Dialog.Actions>
            <Button onPress={() => setShowOptimizeDialog(false)}>Cancel</Button>
            <Button
              mode='contained'
              onPress={() => handleOptimizeJourney(selectedJourney?.id || '')}
              loading={isGenerating}
              disabled={isGenerating}
            >
              {isGenerating ? 'Optimizing...' : 'Optimize Journey'}
            </Button>
          </Dialog.Actions>
        </Dialog>
      </Portal>

      {/* Journey Details Dialog */}
      {renderJourneyDetails()}
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  header: {
    padding: 16,
    backgroundColor: theme => theme.colors.surface,
  },
  headerContent: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  headerTitle: {
    flexDirection: 'row',
    alignItems: 'center',
    flex: 1,
  },
  titleContainer: {
    marginLeft: 16,
    flex: 1,
  },
  title: {
    fontWeight: 'bold',
    marginBottom: 4,
  },
  subtitle: {
    opacity: 0.7,
  },
  createButton: {
    marginLeft: 16,
  },
  searchSection: {
    padding: 16,
    backgroundColor: theme => theme.colors.surface,
  },
  searchRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
  },
  searchContainer: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
  },
  searchInput: {
    flex: 1,
    marginLeft: 8,
  },
  filterButton: {
    minWidth: 100,
  },
  content: {
    flex: 1,
  },
  contentContainer: {
    padding: 16,
    paddingBottom: 32,
  },
  journeyCard: {
    marginBottom: 16,
    borderRadius: 12,
  },
  journeyHeader: {
    marginBottom: 16,
  },
  journeyTitleRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: 8,
  },
  journeyTitle: {
    flex: 1,
    marginRight: 12,
    fontWeight: 'bold',
  },
  journeyBadges: {
    flexDirection: 'row',
    gap: 8,
  },
  journeyDescription: {
    opacity: 0.7,
    lineHeight: 20,
  },
  journeyProgress: {
    marginBottom: 16,
  },
  progressHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 8,
  },
  progressBar: {
    height: 8,
    borderRadius: 4,
  },
  journeyMetrics: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 16,
  },
  metricItem: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  metricText: {
    marginLeft: 6,
    opacity: 0.7,
  },
  focusAreas: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    marginBottom: 16,
  },
  focusChip: {
    marginRight: 8,
    marginBottom: 4,
  },
  journeyActions: {
    flexDirection: 'row',
    gap: 12,
  },
  actionButton: {
    flex: 1,
  },
  emptyState: {
    alignItems: 'center',
    paddingVertical: 48,
  },
  emptyTitle: {
    marginTop: 16,
    marginBottom: 8,
    textAlign: 'center',
  },
  emptySubtitle: {
    textAlign: 'center',
    opacity: 0.7,
    marginBottom: 24,
  },
  emptyButton: {
    marginTop: 16,
  },
  dialog: {
    maxHeight: '90%',
  },
  dialogTitle: {
    fontWeight: 'bold',
  },
  dialogContent: {
    maxHeight: 500,
  },
  overviewCard: {
    padding: 16,
    marginBottom: 16,
    borderRadius: 12,
  },
  overviewHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 16,
  },
  overviewTitle: {
    marginLeft: 12,
    fontWeight: 'bold',
  },
  overviewMetrics: {
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  overviewMetric: {
    alignItems: 'center',
    flex: 1,
  },
  aiScore: {
    color: theme => theme.colors.primary,
    fontWeight: 'bold',
  },
  insightsCard: {
    padding: 16,
    marginBottom: 16,
    borderRadius: 12,
  },
  insightsHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 16,
  },
  insightsTitle: {
    marginLeft: 12,
    fontWeight: 'bold',
  },
  insightItem: {
    marginBottom: 16,
    padding: 12,
    backgroundColor: '#f8f9fa',
    borderRadius: 8,
  },
  insightHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 8,
  },
  confidenceBadge: {
    backgroundColor: theme => theme.colors.primary,
  },
  insightMessage: {
    marginBottom: 8,
    fontWeight: 'bold',
  },
  recommendationsCard: {
    padding: 16,
    marginBottom: 16,
    borderRadius: 12,
  },
  recommendationsHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 16,
  },
  recommendationsTitle: {
    marginLeft: 12,
    fontWeight: 'bold',
  },
  recommendationItem: {
    marginBottom: 16,
    padding: 12,
    backgroundColor: '#f8f9fa',
    borderRadius: 8,
  },
  recommendationHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 8,
  },
  impactText: {
    opacity: 0.7,
  },
  recommendationTitle: {
    fontWeight: 'bold',
    marginBottom: 4,
  },
  recommendationDescription: {
    marginBottom: 8,
    opacity: 0.8,
  },
  recommendationReasoning: {
    opacity: 0.6,
    fontStyle: 'italic',
  },
  weeksCard: {
    padding: 16,
    marginBottom: 16,
    borderRadius: 12,
  },
  weeksHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 16,
  },
  weeksTitle: {
    marginLeft: 12,
    fontWeight: 'bold',
  },
  weekItem: {
    marginBottom: 16,
    padding: 12,
    backgroundColor: '#f8f9fa',
    borderRadius: 8,
  },
  weekHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 8,
  },
  weekTitle: {
    fontWeight: 'bold',
  },
  weekBadges: {
    flexDirection: 'row',
    gap: 8,
  },
  weekDescription: {
    marginBottom: 8,
    opacity: 0.8,
  },
  aiOptimization: {
    opacity: 0.6,
    fontStyle: 'italic',
    fontSize: 12,
  },
});
